'use client';

/**
 * BrandedColorPicker — the canonical CrewSync color picker.
 *
 * Pattern: palette grid → hue slider → brightness slider → hex row with
 * eyedropper + Apply. Branded chrome (rounded corners, soft shadow, teal
 * accents). Never use the browser-native <input type="color"> — that pops
 * an OS dialog that can't be styled.
 *
 * Two ways to mount:
 *   1. <BrandedColorPicker value=... onChange=... /> — bare panel, caller
 *      controls layout (e.g. inline within a settings row).
 *   2. <BrandedColorPickerPopout open=... align=... value=... onChange=... onClose=... />
 *      — anchored popout with click-outside-to-close backdrop.
 *
 * Live-preview contract: onChange fires on every palette swatch, hue/brightness
 * drag, blur of the hex input, and Apply click. onClose fires on swatch pick,
 * Apply, backdrop click, and explicit close. Apply = onChange(current) + onClose().
 */

import { useState } from 'react';
import { Pipette } from 'lucide-react';

export const DEFAULT_PALETTE = [
  '#00C2A8', '#F59E0B', '#EF4444', '#3B82F6', '#A855F7',
  '#10B981', '#EC4899', '#F97316', '#14B8A6', '#6366F1',
  '#0EA5E9', '#22C55E', '#EAB308', '#D946EF', '#64748B',
];

export function hslToHex(h: number, s: number, l: number): string {
  const sN = s / 100;
  const lN = l / 100;
  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lN - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  const toByte = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
}

export function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / d + 2) * 60; break;
      case b: h = ((r - g) / d + 4) * 60; break;
    }
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

interface BrandedColorPickerProps {
  value: string;
  onChange: (c: string) => void;
  onClose?: () => void;
  palette?: string[];
  footnote?: string;
}

export function BrandedColorPicker({
  value,
  onChange,
  onClose,
  palette = DEFAULT_PALETTE,
  footnote,
}: BrandedColorPickerProps) {
  const seed = hexToHsl(value) || { h: 174, s: 100, l: 38 };
  const [hue, setHue] = useState<number>(seed.h);
  const [lightness, setLightness] = useState<number>(seed.l);
  const [hex, setHex] = useState(value);
  const [eyeDropperError, setEyeDropperError] = useState<string | null>(null);

  const liveHex = hslToHex(hue, 100, lightness);

  const applyHex = (commit: boolean) => {
    const v = hex.trim();
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
      onChange(v);
      if (commit && onClose) onClose();
    }
  };

  const openEyeDropper = async () => {
    setEyeDropperError(null);
    if (typeof window === 'undefined' || !('EyeDropper' in window)) {
      setEyeDropperError('Eyedropper requires Chrome, Edge, or Brave. Use the palette or hex input on this browser.');
      return;
    }
    try {
      // @ts-expect-error EyeDropper is a recent platform API not in lib.dom yet.
      const ed = new window.EyeDropper();
      const result = await ed.open();
      if (result?.sRGBHex) {
        const picked = result.sRGBHex.toLowerCase();
        setHex(picked);
        onChange(picked);
        const parsed = hexToHsl(picked);
        if (parsed) { setHue(parsed.h); setLightness(parsed.l); }
      }
    } catch {
      // user dismissed picker
    }
  };

  return (
    <>
      <p className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[--text-tertiary] mb-2.5">Palette</p>
      <div className="grid grid-cols-8 gap-2 mb-4">
        {palette.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => { onChange(c); onClose?.(); }}
            className={`w-7 h-7 rounded-full transition-transform shadow-sm ${value === c ? 'ring-2 ring-offset-2 ring-[#00C2A8] scale-110' : 'hover:scale-110'}`}
            style={{ backgroundColor: c }}
            aria-label={`Set color to ${c}`}
          />
        ))}
      </div>

      <p className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[--text-tertiary] mb-2.5">Hue</p>
      <input
        type="range"
        min={0}
        max={360}
        value={hue}
        onChange={(e) => { const h = Number(e.target.value); setHue(h); onChange(hslToHex(h, 100, lightness)); }}
        aria-label="Hue"
        className="w-full h-3 rounded-full appearance-none cursor-pointer crewsync-hue-slider mb-3"
        style={{
          background: 'linear-gradient(to right, hsl(0 100% 50%), hsl(60 100% 50%), hsl(120 100% 50%), hsl(180 100% 50%), hsl(240 100% 50%), hsl(300 100% 50%), hsl(360 100% 50%))',
        }}
      />

      <p className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[--text-tertiary] mb-2.5">Brightness</p>
      <input
        type="range"
        min={20}
        max={70}
        value={lightness}
        onChange={(e) => { const l = Number(e.target.value); setLightness(l); onChange(hslToHex(hue, 100, l)); }}
        aria-label="Brightness"
        className="w-full h-3 rounded-full appearance-none cursor-pointer crewsync-hue-slider mb-4"
        style={{
          background: `linear-gradient(to right, hsl(${hue} 100% 20%), hsl(${hue} 100% 50%), hsl(${hue} 100% 70%))`,
        }}
      />

      <p className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[--text-tertiary] mb-2.5">Hex</p>
      <div className="flex items-center gap-2">
        <span
          className="w-9 h-9 rounded-xl border border-[--border-primary] shadow-inner shrink-0"
          style={{ backgroundColor: liveHex }}
          aria-hidden
        />
        <button
          type="button"
          onClick={openEyeDropper}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-[--border-primary] bg-white dark:bg-[#1a1a1a] text-[--text-secondary] hover:text-[--text-primary] hover:border-[#00C2A8] transition-colors shrink-0"
          aria-label="Pick a color from anywhere on the screen"
          title="Pick a color from anywhere on the screen (eyedropper)"
        >
          <Pipette className="w-4 h-4" />
        </button>
        <input
          type="text"
          value={hex}
          onChange={(e) => setHex(e.target.value)}
          onBlur={() => applyHex(false)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyHex(true); }}
          placeholder={liveHex}
          className="flex-1 min-w-0 px-3 py-2 text-[12px] rounded-xl border border-[--border-primary] bg-white dark:bg-[#1a1a1a] text-[--text-primary] focus:outline-none focus:border-[#00C2A8] font-mono"
        />
        <button
          type="button"
          onClick={() => applyHex(true)}
          className="px-3 py-2 text-[12px] font-semibold rounded-xl bg-[#00C2A8] text-white hover:bg-[#00a48f] transition-colors shadow-sm"
        >
          Apply
        </button>
      </div>

      {eyeDropperError && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-3 leading-relaxed">
          {eyeDropperError}
        </p>
      )}

      {footnote && (
        <p className="text-[10px] text-[--text-tertiary] mt-3 leading-relaxed">
          {footnote}
        </p>
      )}

      <style jsx global>{`
        input.crewsync-hue-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 9999px;
          background: #ffffff;
          border: 2px solid rgba(0,0,0,0.12);
          box-shadow: 0 2px 6px rgba(0,0,0,0.18);
          cursor: pointer;
        }
        input.crewsync-hue-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 9999px;
          background: #ffffff;
          border: 2px solid rgba(0,0,0,0.12);
          box-shadow: 0 2px 6px rgba(0,0,0,0.18);
          cursor: pointer;
        }
      `}</style>
    </>
  );
}

interface BrandedColorPickerPopoutProps extends BrandedColorPickerProps {
  open: boolean;
  align?: 'left' | 'right';
}

export function BrandedColorPickerPopout({
  open, align = 'right', ...rest
}: BrandedColorPickerPopoutProps) {
  if (!open) return null;
  return (
    <div className="absolute inset-0 z-30">
      <div className="absolute inset-0" onClick={rest.onClose} />
      <div
        className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-2 w-[320px] rounded-2xl border border-[--border-primary] bg-white dark:bg-[#1a1a1a] shadow-2xl p-4 z-40`}
        onClick={(e) => e.stopPropagation()}
      >
        <BrandedColorPicker {...rest} />
      </div>
    </div>
  );
}
